import { useEffect, lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { OrganizationProvider } from "@/hooks/use-organization";
import { FeatureFlagsProvider } from "@/hooks/use-feature-flags";
import { SubscriptionProvider } from "@/hooks/use-subscription";
import { BrandingProvider } from "@/contexts/BrandingContext";
import { DomainProvider, useDomain } from "@/contexts/DomainContext";
import { AuthProvider } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { TrainerLayout } from "@/components/TrainerLayout";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { CookieConsent } from "@/components/CookieConsent";
import { GlobalShortcuts } from "@/components/GlobalShortcuts";
import { FullScreenLoader } from "@/components/PulseLoader";
import { DomainError } from "@/pages/errors/DomainError";
import { initPostHog } from "@/lib/posthog";

// Critical path - small pages that render immediately
import NotFound from "./pages/NotFound";

// Landing/Auth/ResetPassword are lazy-loaded: they're public entry points but
// each user only hits one of them at a time, and Suspense already wraps routes.
const Landing = lazy(() => import("./pages/Landing"));
const Auth = lazy(() => import("./pages/Auth"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));

// Lazy loaded pages - Trainer/Admin
const Dashboard = lazy(() => import("./pages/Dashboard"));
const CompanyDashboard = lazy(() => import("./pages/CompanyDashboard"));
const CompanySettings = lazy(() => import("./pages/CompanySettings"));
const TrainerStats = lazy(() => import("./pages/TrainerStats"));
const Team = lazy(() => import("./pages/Team"));
const Clients = lazy(() => import("./pages/Clients"));
const ClientDetail = lazy(() => import("./pages/ClientDetail"));
const ClientReports = lazy(() => import("./pages/ClientReports"));
const Forecast = lazy(() => import("./pages/Forecast"));
const ProactiveInsights = lazy(() => import("./pages/ProactiveInsights"));
const SpotterReview = lazy(() => import("./pages/SpotterReview"));
const Calendar = lazy(() => import("./pages/Calendar"));
const Settings = lazy(() => import("./pages/Settings"));
const VoiceQualityPage = lazy(() => import("./pages/settings/voice-quality/VoiceQualityPage"));
const VoiceCalibrationWizard = lazy(() => import("./pages/settings/voice-calibration/VoiceCalibrationWizard"));
const RoleplayCalibration = lazy(() => import("./pages/settings/voice-calibration/RoleplayCalibration"));
const Import = lazy(() => import("./pages/Import"));
const Earnings = lazy(() => import("./pages/Earnings"));
const Invoices = lazy(() => import("./pages/Invoices"));
const Leads = lazy(() => import("./pages/Leads"));
const Insights = lazy(() => import("./pages/Insights"));
const Admin = lazy(() => import("./pages/Admin"));
const AdminOrgSettings = lazy(() => import("./pages/AdminOrgSettings"));
const AdminRevenue = lazy(() => import("./pages/AdminRevenue"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const Workouts = lazy(() => import("./pages/Workouts"));
const WorkoutBuilderPage = lazy(() => import("./pages/WorkoutBuilderPage"));
const WorkoutDetail = lazy(() => import("./pages/WorkoutDetail"));
const Programs = lazy(() => import("./pages/Programs"));
const ProgramBuilder = lazy(() => import("./pages/ProgramBuilder"));
const ImportProgram = lazy(() => import("./pages/ImportProgram"));
const Habits = lazy(() => import("./pages/Habits"));
const ProgramTemplates = lazy(() => import("./pages/ProgramTemplates"));
const CheckIns = lazy(() => import("./pages/CheckIns"));
const VideoLibrary = lazy(() => import("./pages/VideoLibrary"));
const Billing = lazy(() => import("./pages/Billing"));
const Messages = lazy(() => import("./pages/Messages"));
const SMSInbox = lazy(() => import("./pages/SMSInbox"));
const Community = lazy(() => import("./pages/Community"));
const ProfileBuilder = lazy(() => import("./pages/ProfileBuilder"));
const Referrals = lazy(() => import("./pages/Referrals"));
const FinancialIntelligence = lazy(() => import("./pages/FinancialIntelligence"));
const SimulationLab = lazy(() => import("./pages/SimulationLab"));
const EmployeeROI = lazy(() => import("./pages/EmployeeROI"));
const Integrations = lazy(() => import("./pages/Integrations"));
const HealthDataHub = lazy(() => import("./pages/HealthDataHub"));
const RoutePlanner = lazy(() => import("./pages/RoutePlanner"));
const LocationIntelligence = lazy(() => import("./pages/LocationIntelligence"));
const MatchingDashboard = lazy(() => import("./pages/MatchingDashboard"));

// Marketing pages
const ContentStudio = lazy(() => import("./pages/marketing/ContentStudio"));
const MainSiteEditor = lazy(() => import("./pages/marketing/MainSiteEditor"));
const EmailCampaigns = lazy(() => import("./pages/marketing/EmailCampaigns"));
const EmailCampaignsSimple = lazy(() => import("./pages/marketing/EmailCampaignsSimple"));
const LeadMagnets = lazy(() => import("./pages/marketing/LeadMagnets"));
const Automations = lazy(() => import("./pages/marketing/Automations"));
const AutomationsSimple = lazy(() => import("./pages/marketing/AutomationsSimple"));
const SocialMedia = lazy(() => import("./pages/marketing/SocialMedia"));
const ROIDashboard = lazy(() => import("./pages/marketing/ROIDashboard"));
const MarketingAgent = lazy(() => import("./pages/marketing/MarketingAgent"));
const LandingPages = lazy(() => import("./pages/marketing/LandingPages"));
const LandingPageBuilder = lazy(() => import("./pages/marketing/LandingPageBuilder"));
const MediaLibraryPage = lazy(() => import("./pages/marketing/MediaLibraryPage"));
const StoryStudio = lazy(() => import("./pages/marketing/StoryStudio"));
const Campaigns = lazy(() => import("./pages/marketing/Campaigns"));
const CampaignDetail = lazy(() => import("./pages/marketing/CampaignDetail"));

// Public landing page
const PublicLandingPage = lazy(() => import("./pages/public/LandingPage"));

// Public main site (Initiative 5 — trainer's "always on" front door)
const PublicMainSite = lazy(() => import("./pages/public/MainSite"));

// Lazy loaded pages - Client Portal
const PortalAuth = lazy(() => import("./pages/portal/PortalAuth"));
const PortalLayout = lazy(() => import("./pages/portal/PortalLayout"));
const PortalDashboard = lazy(() => import("./pages/portal/Dashboard"));
const PortalBook = lazy(() => import("./pages/portal/Book"));
const PortalMeasurements = lazy(() => import("./pages/portal/Measurements"));
const PortalSessionHistory = lazy(() => import("./pages/portal/SessionHistory"));
const PortalProgress = lazy(() => import("./pages/portal/Progress"));
const PortalWorkout = lazy(() => import("./pages/portal/Workout"));
const PortalWorkoutHistory = lazy(() => import("./pages/portal/WorkoutHistory"));
const PortalDataExport = lazy(() => import("./pages/portal/DataExport"));
const PortalNutrition = lazy(() => import("./pages/portal/Nutrition"));
const PortalBilling = lazy(() => import("./pages/portal/Billing"));
const PortalPackages = lazy(() => import("./pages/portal/Packages"));
const PortalPhotoSubmission = lazy(() => import("./pages/portal/PhotoSubmission"));

// Lazy loaded pages - Legal & Public
const Terms = lazy(() => import("./pages/legal/Terms"));
const Privacy = lazy(() => import("./pages/legal/Privacy"));
const RefundPolicy = lazy(() => import("./pages/legal/RefundPolicy"));
const Cookies = lazy(() => import("./pages/legal/Cookies"));
const AcceptableUse = lazy(() => import("./pages/legal/AcceptableUse"));
const HelpCenter = lazy(() => import("./pages/HelpCenter"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));

// Public trainer profile
const TrainerProfile = lazy(() => import("./pages/public/TrainerProfile"));

// Marketplace public pages
const FindTrainer = lazy(() => import("./pages/marketplace/FindTrainer"));
const MatchQuiz = lazy(() => import("./pages/marketplace/MatchQuiz"));
const MatchResults = lazy(() => import("./pages/marketplace/MatchResults"));
const BookTrainer = lazy(() => import("./pages/marketplace/BookTrainer"));

// Loading fallback for lazy loaded components - uses branded PulseLoader
function PageLoader() {
  return <FullScreenLoader />;
}

/**
 * React Query defaults.
 *
 * This was a bare `new QueryClient()`, which meant every default applied —
 * and two of them hurt badly during the 2026-08-31 outage.
 *
 * `staleTime: 0` (the default) marks every result stale the moment it lands,
 * so any remount, window refocus, or reconnect refetches it. With ~198
 * useQuery call sites, most of them without their own staleTime, that is a
 * lot of repeat reads against the database for data that has not changed.
 *
 * `retry: 3` with exponential backoff is the worse one. When the database was
 * unreachable each attempt hung ~20s before Cloudflare gave up, so a failing
 * query took 90+ seconds to surface an error. The app showed a spinner that
 * whole time instead of saying anything — "perma-loading" rather than
 * "we can't reach the server". An outage should be visible in seconds.
 *
 * The values below are deliberately conservative: they cut redundant reads
 * and make failure legible without changing what any individual query does.
 * Call sites that set their own staleTime still win.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Trainer-facing data (clients, sessions, programs) does not change
      // second-to-second, and anything that truly must be live either sets a
      // shorter staleTime itself or uses a realtime subscription.
      staleTime: 60_000,
      // Keep unmounted data around briefly so tab-switching re-renders from
      // cache instead of re-querying.
      gcTime: 5 * 60_000,
      // Refetching on every window focus is the single biggest source of
      // redundant reads in a dashboard people leave open all day. staleTime
      // already covers genuinely-stale data on remount.
      refetchOnWindowFocus: false,
      // One retry absorbs a transient blip; three turns a hard outage into a
      // 90-second spinner. Never retry an auth/permission failure — those are
      // answers, not blips, and retrying only delays the real message.
      retry: (failureCount, error) => {
        const status = (error as { status?: number })?.status;
        if (status === 401 || status === 403) return false;
        return failureCount < 1;
      },
      retryDelay: 1_000,
    },
    mutations: {
      // A failed write must surface immediately: the user is waiting on it,
      // and a silent retry of a non-idempotent mutation is its own hazard.
      retry: false,
    },
  },
});

/**
 * White-label routes - shown only on subdomains and custom domains
 * Limited set of routes (portal, auth, trainer dashboard) without landing/blog/admin
 */
function WhiteLabelRoutes() {
  const { organization, error, domainInfo } = useDomain();

  // Show error page if domain is not configured
  if (error) {
    return <DomainError error={error} hostname={domainInfo.hostname} />;
  }

  const defaultRoute = organization?.defaultRoute || "/portal";

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Auth routes */}
        <Route path="/auth" element={<Auth />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/onboarding" element={<Onboarding />} />

        {/* Legal pages - always accessible */}
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/refund-policy" element={<RefundPolicy />} />
        <Route path="/cookies" element={<Cookies />} />
        <Route path="/acceptable-use" element={<AcceptableUse />} />
        <Route path="/help" element={<HelpCenter />} />
        <Route path="/support" element={<HelpCenter />} />

        {/* Client Portal Routes */}
        <Route path="/portal" element={<PortalAuth />} />
        <Route element={<PortalLayout />}>
          <Route path="/portal/dashboard" element={<PortalDashboard />} />
          <Route path="/portal/book" element={<PortalBook />} />
          <Route path="/portal/sessions" element={<PortalSessionHistory />} />
          <Route path="/portal/measurements" element={<PortalMeasurements />} />
          <Route path="/portal/progress" element={<PortalProgress />} />
          <Route path="/portal/photos/submit" element={<PortalPhotoSubmission />} />
          <Route path="/portal/workout" element={<PortalWorkout />} />
          <Route path="/portal/workout/history" element={<PortalWorkoutHistory />} />
          <Route path="/portal/nutrition" element={<PortalNutrition />} />
          <Route path="/portal/billing" element={<PortalBilling />} />
          <Route path="/portal/packages" element={<PortalPackages />} />
          <Route path="/portal/my-data" element={<PortalDataExport />} />
        </Route>

        {/* Trainer Dashboard - available on white-label domains for org trainers */}
        <Route element={<TrainerLayout />}>
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/company" element={<CompanyDashboard />} />
          <Route path="/company/settings" element={<CompanySettings />} />
          <Route path="/company/trainer/:userId" element={<TrainerStats />} />
          <Route path="/team" element={<Team />} />
          <Route path="/clients" element={<Clients />} />
          <Route path="/clients/reports" element={<ClientReports />} />
          <Route path="/clients/forecast" element={<Forecast />} />
          <Route path="/clients/:id" element={<ClientDetail />} />
          <Route path="/calendar" element={<Calendar />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/settings/voice-quality" element={<VoiceQualityPage />} />
          <Route path="/settings/voice-calibration" element={<VoiceCalibrationWizard />} />
          <Route path="/settings/voice-calibration/roleplay" element={<RoleplayCalibration />} />
          <Route path="/billing" element={<Billing />} />
          <Route path="/import" element={<Import />} />
          <Route path="/earnings" element={<Earnings />} />
          <Route path="/invoices" element={<Invoices />} />
          <Route path="/leads" element={<Leads />} />
          <Route path="/insights" element={<Insights />} />
          <Route path="/proactive-insights" element={<ProactiveInsights />} />
          <Route path="/spotter-review" element={<SpotterReview />} />
          <Route path="/financial-intelligence" element={<FinancialIntelligence />} />
          <Route path="/simulations" element={<SimulationLab />} />
          <Route path="/employee-roi" element={<EmployeeROI />} />
          <Route path="/integrations" element={<Integrations />} />
          <Route path="/health" element={<HealthDataHub />} />
          <Route path="/routes" element={<RoutePlanner />} />
          <Route path="/location-intelligence" element={<LocationIntelligence />} />
          <Route path="/matching" element={<MatchingDashboard />} />
          <Route path="/messages" element={<Messages />} />
          <Route path="/sms" element={<SMSInbox />} />
          <Route path="/community" element={<Community />} />
          <Route path="/workouts" element={<Workouts />} />
          <Route path="/workouts/new" element={<WorkoutBuilderPage />} />
          <Route path="/workouts/:id" element={<WorkoutDetail />} />
          <Route path="/workouts/:id/edit" element={<WorkoutBuilderPage />} />
          <Route path="/programs" element={<Programs />} />
          <Route path="/programs/import" element={<ImportProgram />} />
          <Route path="/programs/new" element={<ProgramBuilder />} />
          <Route path="/programs/:id" element={<ProgramBuilder />} />
          <Route path="/habits" element={<Habits />} />
          <Route path="/templates" element={<ProgramTemplates />} />
          <Route path="/check-ins" element={<CheckIns />} />
          <Route path="/video-library" element={<VideoLibrary />} />
          <Route path="/profile" element={<ProfileBuilder />} />
          <Route path="/referrals" element={<Referrals />} />
          {/* Marketing routes */}
          <Route path="/marketing/content" element={<ContentStudio />} />
          <Route path="/marketing/emails" element={<EmailCampaignsSimple />} />
          <Route path="/marketing/emails/advanced" element={<EmailCampaigns />} />
          <Route path="/marketing/story-studio" element={<StoryStudio />} />
          <Route path="/marketing/lead-magnets" element={<LeadMagnets />} />
          <Route path="/marketing/automations" element={<AutomationsSimple />} />
          <Route path="/marketing/automations/advanced" element={<Automations />} />
          <Route path="/marketing/social" element={<SocialMedia />} />
          <Route path="/marketing/roi" element={<ROIDashboard />} />
          <Route path="/marketing/agent" element={<MarketingAgent />} />
          <Route path="/marketing/pages" element={<LandingPages />} />
          <Route path="/marketing/pages/:id/edit" element={<LandingPageBuilder />} />
          <Route path="/marketing/site" element={<MainSiteEditor />} />
          <Route path="/marketing/media" element={<MediaLibraryPage />} />
          <Route path="/marketing/campaigns" element={<Campaigns />} />
          <Route path="/marketing/campaigns/:id" element={<CampaignDetail />} />
        </Route>

        {/* Public landing pages */}
        <Route path="/p/:slug" element={<PublicLandingPage />} />

        {/* Public main sites (Initiative 5) */}
        <Route path="/site/:slug" element={<PublicMainSite />} />

        {/* Root redirects to default route (usually /portal) */}
        <Route path="/" element={<Navigate to={defaultRoute} replace />} />

        {/* Catch-all - redirect to default route */}
        <Route path="*" element={<Navigate to={defaultRoute} replace />} />
      </Routes>
    </Suspense>
  );
}

/**
 * Main app routes - shown on trainerpulse.app
 * Full set of routes including landing page, blog, admin, etc.
 */
function MainAppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Auth routes - no guard */}
        <Route path="/auth" element={<Auth />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/onboarding" element={<Onboarding />} />

        {/* Legal pages - public */}
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/refund-policy" element={<RefundPolicy />} />
        <Route path="/cookies" element={<Cookies />} />
        <Route path="/acceptable-use" element={<AcceptableUse />} />
        <Route path="/help" element={<HelpCenter />} />
        <Route path="/support" element={<HelpCenter />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />

        {/* Public trainer profile */}
        <Route path="/t/:username" element={<TrainerProfile />} />

        {/* Marketplace public routes */}
        <Route path="/find-trainer" element={<FindTrainer />} />
        <Route path="/find-trainer/quiz" element={<MatchQuiz />} />
        <Route path="/find-trainer/matches" element={<MatchResults />} />
        <Route path="/find-trainer/:username" element={<TrainerProfile />} />
        <Route path="/find-trainer/:username/book" element={<BookTrainer />} />

        {/* Public landing pages */}
        <Route path="/p/:slug" element={<PublicLandingPage />} />

        {/* Public main sites (Initiative 5) */}
        <Route path="/site/:slug" element={<PublicMainSite />} />

        {/* Client Portal Routes - separate flow */}
        <Route path="/portal" element={<PortalAuth />} />
        <Route element={<PortalLayout />}>
          <Route path="/portal/dashboard" element={<PortalDashboard />} />
          <Route path="/portal/book" element={<PortalBook />} />
          <Route path="/portal/sessions" element={<PortalSessionHistory />} />
          <Route path="/portal/measurements" element={<PortalMeasurements />} />
          <Route path="/portal/progress" element={<PortalProgress />} />
          <Route path="/portal/photos/submit" element={<PortalPhotoSubmission />} />
          <Route path="/portal/workout" element={<PortalWorkout />} />
          <Route path="/portal/workout/history" element={<PortalWorkoutHistory />} />
          <Route path="/portal/nutrition" element={<PortalNutrition />} />
          <Route path="/portal/billing" element={<PortalBilling />} />
          <Route path="/portal/packages" element={<PortalPackages />} />
          <Route path="/portal/my-data" element={<PortalDataExport />} />
        </Route>

        {/* Public Landing Page */}
        <Route path="/" element={<Landing />} />

        {/* Trainer/Admin routes - Layout stays mounted across navigations */}
        <Route element={<TrainerLayout />}>
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/company" element={<CompanyDashboard />} />
          <Route path="/company/settings" element={<CompanySettings />} />
          <Route path="/company/trainer/:userId" element={<TrainerStats />} />
          <Route path="/team" element={<Team />} />
          <Route path="/clients" element={<Clients />} />
          <Route path="/clients/reports" element={<ClientReports />} />
          <Route path="/clients/forecast" element={<Forecast />} />
          <Route path="/clients/:id" element={<ClientDetail />} />
          <Route path="/calendar" element={<Calendar />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/settings/voice-quality" element={<VoiceQualityPage />} />
          <Route path="/settings/voice-calibration" element={<VoiceCalibrationWizard />} />
          <Route path="/settings/voice-calibration/roleplay" element={<RoleplayCalibration />} />
          <Route path="/billing" element={<Billing />} />
          <Route path="/import" element={<Import />} />
          <Route path="/earnings" element={<Earnings />} />
          <Route path="/invoices" element={<Invoices />} />
          <Route path="/leads" element={<Leads />} />
          <Route path="/insights" element={<Insights />} />
          <Route path="/proactive-insights" element={<ProactiveInsights />} />
          <Route path="/spotter-review" element={<SpotterReview />} />
          <Route path="/financial-intelligence" element={<FinancialIntelligence />} />
          <Route path="/simulations" element={<SimulationLab />} />
          <Route path="/employee-roi" element={<EmployeeROI />} />
          <Route path="/integrations" element={<Integrations />} />
          <Route path="/health" element={<HealthDataHub />} />
          <Route path="/routes" element={<RoutePlanner />} />
          <Route path="/location-intelligence" element={<LocationIntelligence />} />
          <Route path="/matching" element={<MatchingDashboard />} />
          <Route path="/messages" element={<Messages />} />
          <Route path="/sms" element={<SMSInbox />} />
          <Route path="/community" element={<Community />} />
          <Route path="/workouts" element={<Workouts />} />
          <Route path="/workouts/new" element={<WorkoutBuilderPage />} />
          <Route path="/workouts/:id" element={<WorkoutDetail />} />
          <Route path="/workouts/:id/edit" element={<WorkoutBuilderPage />} />
          <Route path="/programs" element={<Programs />} />
          <Route path="/programs/import" element={<ImportProgram />} />
          <Route path="/programs/new" element={<ProgramBuilder />} />
          <Route path="/programs/:id" element={<ProgramBuilder />} />
          <Route path="/habits" element={<Habits />} />
          <Route path="/templates" element={<ProgramTemplates />} />
          <Route path="/check-ins" element={<CheckIns />} />
          <Route path="/video-library" element={<VideoLibrary />} />
          <Route path="/profile" element={<ProfileBuilder />} />
          <Route path="/referrals" element={<Referrals />} />
          {/* Marketing routes */}
          <Route path="/marketing/content" element={<ContentStudio />} />
          <Route path="/marketing/emails" element={<EmailCampaignsSimple />} />
          <Route path="/marketing/emails/advanced" element={<EmailCampaigns />} />
          <Route path="/marketing/story-studio" element={<StoryStudio />} />
          <Route path="/marketing/lead-magnets" element={<LeadMagnets />} />
          <Route path="/marketing/automations" element={<AutomationsSimple />} />
          <Route path="/marketing/automations/advanced" element={<Automations />} />
          <Route path="/marketing/social" element={<SocialMedia />} />
          <Route path="/marketing/roi" element={<ROIDashboard />} />
          <Route path="/marketing/agent" element={<MarketingAgent />} />
          <Route path="/marketing/pages" element={<LandingPages />} />
          <Route path="/marketing/pages/:id/edit" element={<LandingPageBuilder />} />
          <Route path="/marketing/site" element={<MainSiteEditor />} />
          <Route path="/marketing/media" element={<MediaLibraryPage />} />
          <Route path="/marketing/campaigns" element={<Campaigns />} />
          <Route path="/marketing/campaigns/:id" element={<CampaignDetail />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/admin/revenue" element={<AdminRevenue />} />
          <Route path="/admin/org/:orgId" element={<AdminOrgSettings />} />
        </Route>

        {/* Catch-all */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}

/**
 * Domain-aware routing - chooses between white-label and main app routes
 */
function DomainAwareRoutes() {
  const { isWhiteLabel, loading } = useDomain();

  // Show loader while domain is being detected
  if (loading) {
    return <PageLoader />;
  }

  // White-label domains get limited routes
  if (isWhiteLabel) {
    return <WhiteLabelRoutes />;
  }

  // Main app gets full routes
  return <MainAppRoutes />;
}

const App = () => {
  // Initialize PostHog. Theme (light default, dark opt-in) is managed by ThemeProvider.
  useEffect(() => {
    initPostHog();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ErrorBoundary>
        {/* ThemeProvider applies `.light` class to <html> on mount.
            Default is light; dark mode is opt-in via useTheme().setTheme('dark'). */}
        <ThemeProvider>
          {/* DomainProvider must be outside OrganizationProvider for domain detection before auth */}
          <DomainProvider>
            <AuthProvider>
              <OrganizationProvider>
                <BrandingProvider>
                  <SubscriptionProvider>
                    <FeatureFlagsProvider>
                    <TooltipProvider>
                      <Toaster />
                      <Sonner />
                      <BrowserRouter>
                        <CookieConsent />
                        {/* Domain-aware routing handles white-label vs main app */}
                        <DomainAwareRoutes />
                        {/* Global keyboard shortcuts + command palette.
                            Only active when a user is authenticated. */}
                        <GlobalShortcuts />
                      </BrowserRouter>
                    </TooltipProvider>
                    </FeatureFlagsProvider>
                  </SubscriptionProvider>
                </BrandingProvider>
              </OrganizationProvider>
            </AuthProvider>
          </DomainProvider>
        </ThemeProvider>
      </ErrorBoundary>
    </QueryClientProvider>
  );
};

export default App;
